import {
  Box,
  Collapse,
  Group,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  Camera,
  CaretDown,
  CaretUp,
  Check,
  ClipboardText,
  Gear,
  type Icon,
  Lightning,
  MagnifyingGlass,
  Pulse,
  ShieldCheck,
  Wrench,
  X,
} from '@phosphor-icons/react';
import { useState } from 'react';

export type WorkOrderTask = {
  id: string;
  description: string;
  groupId?: string | null;
  groupLabel?: string | null;
  typeKey?: string | null;
  typeName: string;
  status: 'incomplete' | 'complete';
  completedAt?: string | Date | null;
};

const taskTypeIcons: Record<string, Icon> = {
  review: ClipboardText,
  safety: ShieldCheck,
  diagnose: Wrench,
  inspect: MagnifyingGlass,
  photo: Camera,
  repair: Gear,
  config: Lightning,
  test: Pulse,
};

function getTaskIcon(task: WorkOrderTask): Icon {
  const key = task.typeKey?.toLowerCase() ?? task.typeName.toLowerCase();

  return (
    Object.entries(taskTypeIcons).find(([match]) => key.includes(match))?.[1] ??
    ClipboardText
  );
}

function isComplete(task: WorkOrderTask) {
  return Boolean(task.completedAt || task.status?.toLowerCase() === 'complete');
}

function groupTasks(tasks: WorkOrderTask[]) {
  const grouped = new Map<string, WorkOrderTask[]>();

  for (const task of tasks) {
    const group = task.groupLabel || 'Ungrouped';
    grouped.set(group, [...(grouped.get(group) ?? []), task]);
  }

  return [...grouped.entries()];
}

export function WorkOrderTaskList({ tasks }: { tasks: WorkOrderTask[] }) {
  const groups = groupTasks(tasks);

  return (
    <Box>
      <Table verticalSpacing={0} horizontalSpacing="sm" withRowBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={48} />
            <Table.Th></Table.Th>
            <Table.Th w={96} ta="center"></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {groups.map(([groupLabel, groupTasks]) => (
            <TaskGroup key={groupLabel} label={groupLabel} tasks={groupTasks} />
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

function TaskGroup({
  label,
  tasks,
}: {
  label: string;
  tasks: WorkOrderTask[];
}) {
  const [opened, setOpened] = useState(true);
  const completed = tasks.filter(isComplete).length;
  const Caret = opened ? CaretUp : CaretDown;

  return (
    <>
      <Table.Tr bg="gray.0">
        <Table.Td colSpan={3} p={0}>
          <UnstyledButton w="100%" onClick={() => setOpened((v) => !v)}>
            <Group justify="space-between" px="sm" py={8}>
              <Text fw={700} size="sm">
                {label}
              </Text>

              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  {completed} of {tasks.length}
                </Text>
                <Caret size={16} />
              </Group>
            </Group>
          </UnstyledButton>
        </Table.Td>
      </Table.Tr>

      <Table.Tr>
        <Table.Td colSpan={3} p={0}>
          <Collapse expanded={opened}>
            <Table verticalSpacing={0} horizontalSpacing="sm">
              <Table.Tbody>
                {tasks.map((task) => {
                  const Icon = getTaskIcon(task);
                  const complete = isComplete(task);

                  return (
                    <Table.Tr key={task.id}>
                      <Table.Td w={48} py={8}>
                        <Icon size={20} weight="regular" />
                      </Table.Td>

                      <Table.Td py={8}>
                        <Text size="sm">{task.description}</Text>
                      </Table.Td>

                      <Table.Td w={96} py={8} ta="center">
                        {complete ? (
                          <Check size={18} weight="bold" color="green" />
                        ) : (
                          <X size={18} weight="bold" color="red" />
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}
